import axios from "../../axios";
import { ADMIN_ROLES, normalizeAdminRole } from "../../utils/adminSession";
import {
  DEFAULT_FEATURE_FLAGS,
  GLOBAL_TENANT_ASSIGNMENT,
  GLOBAL_TENANT_ID,
  SUPER_ADMIN_FEATURE_CATALOG,
} from "./constants";

const resolveId = (record = {}) =>
  record.id || record._id || record.tenantId || record.adminId || record.instituteId || "";

const resolveInstituteId = (admin = {}) => {
  const institute =
    admin?.institute && typeof admin.institute === "object"
      ? admin.institute
      : {};

  return (
    admin.instituteId ||
    institute.instituteId ||
    admin.instituteCode ||
    institute.instituteCode ||
    institute._id ||
    institute.id ||
    admin._id ||
    admin.id ||
    ""
  );
};

const normalizeStatus = (status) =>
  status === "inactive" ? "inactive" : "active";

const normalizeAdhyayiProvisioning = (provisioning = {}) => ({
  status: provisioning.status || "not_required",
  tenantId: provisioning.tenantId || "",
  adminUserId: provisioning.adminUserId || "",
  lastError: provisioning.lastError || "",
  provisionedAt: provisioning.provisionedAt || null,
  lastAttemptAt: provisioning.lastAttemptAt || null,
});

const normalizeTenant = (tenant = {}) => ({
  ...tenant,
  id: resolveId(tenant),
  tenantName: tenant.tenantName || tenant.name || "",
  appName: tenant.appName || tenant.app || "",
  instituteName: tenant.instituteName || tenant.institute || "",
  code: tenant.code || "",
  status: normalizeStatus(tenant.status),
  createdAt: tenant.createdAt || null,
  updatedAt: tenant.updatedAt || tenant.createdAt || null,
});

const normalizeInstituteAssignment = (admin = {}) => {
  const institute = admin.institute || admin;
  const address = institute.address || {};
  const addressText = [
    address.addressLine,
    address.city,
    address.state,
    address.pincode,
  ]
    .filter(Boolean)
    .join(", ");

  return {
    tenantName:
      admin.assignedTenantName ||
      admin.assignedTenant?.tenantName ||
      institute.name ||
      "Unassigned",
    appName:
      admin.assignedAppName ||
      admin.assignedTenant?.appName ||
      institute.email ||
      "-",
    instituteName:
      admin.assignedInstituteName ||
      admin.assignedTenant?.instituteName ||
      addressText ||
      "-",
  };
};

const getTenantAssignment = ({
  role,
  assignedTenantId,
  assignedTenant,
  tenants = [],
}) => {
  if (role === ADMIN_ROLES.SUPER_ADMIN || assignedTenantId === GLOBAL_TENANT_ID) {
    return { ...GLOBAL_TENANT_ASSIGNMENT };
  }

  if (assignedTenant) {
    return normalizeTenant(assignedTenant);
  }

  return tenants.find((tenant) => tenant.id === assignedTenantId) || null;
};

const normalizeAdmin = (admin = {}, tenants = []) => {
  const owner = admin.owner || {};
  const institute =
    admin.institute && typeof admin.institute === "object"
      ? admin.institute
      : admin;
  const instituteId = resolveInstituteId(admin);
  const role = normalizeAdminRole(admin.role || admin.adminRole);
  const assignedTenantId =
    role === ADMIN_ROLES.SUPER_ADMIN
      ? GLOBAL_TENANT_ID
      : String(
          admin.assignedTenantId ||
            admin?.assignedTenant?.id ||
            admin?.assignedTenant?._id ||
            "",
        ).trim();
  const assignment = getTenantAssignment({
    role,
    assignedTenantId,
    assignedTenant: admin.assignedTenant,
    tenants,
  });
  const instituteAssignment = normalizeInstituteAssignment(admin);

  return {
    ...admin,
    id: resolveId(admin),
    owner,
    institute: {
      ...institute,
      id: instituteId,
      instituteId,
    },
    instituteId,
    fullName: admin.fullName || owner.name || admin.name || admin.userName || "",
    email: admin.email || owner.email || institute.credentialEmail || "",
    phoneNumber: admin.phoneNumber || owner.mobile || institute.mobile || admin.phone || "",
    role,
    status: normalizeStatus(admin.status),
    isSubscribed: Boolean(institute.isSubscribed),
    planStatus: institute.planStatus || institute.plan?.status || "-",
    paymentAmount: institute.payment?.amount ?? "",
    paymentStatus: institute.payment?.paymentStatus || "-",
    studentCount: institute.studentCount ?? 0,
    assignedTenantId,
    assignedTenant: assignment,
    assignedTenantName:
      assignment?.tenantName || instituteAssignment.tenantName,
    assignedAppName: assignment?.appName || instituteAssignment.appName,
    assignedInstituteName:
      assignment?.instituteName || instituteAssignment.instituteName,
    adhyayiProvisioning: normalizeAdhyayiProvisioning(
      admin.adhyayiProvisioning,
    ),
    createdAt: admin.createdAt || null,
    updatedAt: admin.updatedAt || admin.createdAt || null,
  };
};

const normalizeFeatureFlags = (featureFlags = {}) =>
  SUPER_ADMIN_FEATURE_CATALOG.reduce(
    (accumulator, feature) => ({
      ...accumulator,
      [feature.key]:
        featureFlags[feature.key] === undefined
          ? DEFAULT_FEATURE_FLAGS[feature.key]
          : Boolean(featureFlags[feature.key]),
    }),
    {},
  );

const buildAdminSummary = (admins = []) => ({
  totalAdmins: admins.length,
  activeAdmins: admins.filter((admin) => admin.status === "active").length,
  inactiveAdmins: admins.filter((admin) => admin.status === "inactive").length,
  superAdmins: admins.filter((admin) => admin.role === ADMIN_ROLES.SUPER_ADMIN)
    .length,
});

const normalizeFeaturePayload = (result = {}) => {
  const tenant = result.tenant ? normalizeTenant(result.tenant) : null;
  const featureFlags = normalizeFeatureFlags(result.featureFlags);

  return {
    ...result,
    tenant,
    featureCatalog: SUPER_ADMIN_FEATURE_CATALOG,
    featureFlags,
    enabledCount: Object.values(featureFlags).filter(Boolean).length,
    totalCount: SUPER_ADMIN_FEATURE_CATALOG.length,
    updatedAt: result.updatedAt || tenant?.updatedAt || null,
  };
};

const buildAdminQueryParams = ({
  page = 1,
  limit = 10,
  search = "",
  planStatus = "all",
  paymentStatus = "all",
  isSubscribed = "all",
} = {}) => {
  const params = {
    page,
    limit,
  };

  if (String(search).trim()) {
    params.search = String(search).trim();
  }

  if (planStatus && planStatus !== "all") {
    params.planStatus = planStatus;
  }

  if (paymentStatus && paymentStatus !== "all") {
    params.paymentStatus = paymentStatus;
  }

  if (isSubscribed && isSubscribed !== "all") {
    params.isSubscribed = isSubscribed;
  }

  return params;
};

const buildWhiteLabelAdminQueryParams = ({
  page = 1,
  limit = 10,
  search = "",
  role = "all",
  status = "all",
  tenantId = "all",
} = {}) => {
  const params = {
    page,
    limit,
  };

  if (String(search).trim()) {
    params.search = String(search).trim();
  }

  if (role && role !== "all") {
    params.role = role;
  }

  if (status && status !== "all") {
    params.status = status;
  }

  if (tenantId && tenantId !== "all") {
    params.tenantId = tenantId;
  }

  return params;
};

const extractListItems = (data = {}) => {
  if (Array.isArray(data?.result?.items)) {
    return data.result.items;
  }

  if (Array.isArray(data?.result)) {
    return data.result;
  }

  if (Array.isArray(data?.items)) {
    return data.items;
  }

  if (Array.isArray(data?.institutes)) {
    return data.institutes;
  }

  if (Array.isArray(data?.data)) {
    return data.data;
  }

  return [];
};

const normalizePlan = (plan = {}) => {
  const id = String(
    plan.id ||
      plan._id ||
      plan.planId ||
      plan.value ||
      plan.code ||
      "",
  ).trim();
  const name =
    plan.name ||
    plan.planName ||
    plan.title ||
    plan.label ||
    plan.displayName ||
    id;
  const amount = plan.amount ?? plan.price ?? plan.fee ?? "";

  return {
    ...plan,
    id,
    name,
    amount,
  };
};

const extractPlans = (data = {}) => {
  if (Array.isArray(data?.result?.items)) {
    return data.result.items;
  }

  if (Array.isArray(data?.result?.plans)) {
    return data.result.plans;
  }

  if (Array.isArray(data?.result)) {
    return data.result;
  }

  if (Array.isArray(data?.data?.items)) {
    return data.data.items;
  }

  if (Array.isArray(data?.data?.plans)) {
    return data.data.plans;
  }

  if (Array.isArray(data?.data)) {
    return data.data;
  }

  if (Array.isArray(data?.plans)) {
    return data.plans;
  }

  if (Array.isArray(data?.items)) {
    return data.items;
  }

  return [];
};

const extractStudentCount = (data = {}) => {
  const sources = [data?.result, data?.data, data].filter(Boolean);
  const countKeys = [
    "studentCount",
    "studentsCount",
    "totalStudents",
    "totalStudent",
    "count",
    "total",
    "totalItems",
  ];

  for (const source of sources) {
    if (Array.isArray(source)) {
      return source.length;
    }

    for (const key of countKeys) {
      if (source[key] !== undefined && source[key] !== null && source[key] !== "") {
        return Number(source[key]) || 0;
      }
    }

    if (Array.isArray(source.students)) {
      return source.students.length;
    }

    if (Array.isArray(source.items)) {
      return source.items.length;
    }

    if (source.pagination?.totalItems !== undefined) {
      return Number(source.pagination.totalItems) || 0;
    }
  }
  

  return 0;
};

const fetchInstituteStudentCount = async (instituteId) => {
  if (!instituteId) {
    return 0;
  }

  const { data } = await axios.get("/institute/students", {
    params: { instituteId },
    data: { instituteId },
  });

  return extractStudentCount(data);
};

const withLiveStudentCounts = async (admins = []) => {
  const countResults = await Promise.allSettled(
    admins.map((admin) => fetchInstituteStudentCount(resolveInstituteId(admin))),
  );

  return admins.map((admin, index) => {
    const countResult = countResults[index];

    if (countResult.status !== "fulfilled") {
      return admin;
    }

    return {
      ...admin,
      studentCount: countResult.value,
      institute: {
        ...admin.institute,
        studentCount: countResult.value,
      },
    };
  });
};

export const superAdminApi = {
  async getTenants({ search = "" } = {}) {
    const params = String(search).trim() ? { search: String(search).trim() } : {};
    const { data } = await axios.get("/tenants", { params });
    const items = Array.isArray(data?.result?.items)
      ? data.result.items.map(normalizeTenant)
      : [];

    return {
      ...data,
      result: {
        ...(data?.result || {}),
        items,
        totalItems: data?.result?.totalItems ?? items.length,
      },
    };
  },

  async createTenant(payload) {
    const { data } = await axios.post("/tenants", payload);

    return {
      ...data,
      result: normalizeTenant(data?.result || {}),
    };
  },

  async getInstitutePlans() {
    const { data } = await axios.get("/institute/plans");
    const items = extractPlans(data)
      .map(normalizePlan)
      .filter((plan) => plan.id);

    return {
      ...data,
      result: {
        ...(data?.result && !Array.isArray(data.result) ? data.result : {}),
        items,
      },
    };
  },

  async getAdmins(filters = {}) {
    const { data } = await axios.get("/institute", {
      params: buildAdminQueryParams(filters),
    });
    const items = await withLiveStudentCounts(
      extractListItems(data).map((admin) => normalizeAdmin(admin)),
    );
    const paginationSource = data?.result?.pagination || data?.pagination || {};

    return {
      ...data,
      result: {
        ...(data?.result || {}),
        items,
        pagination: {
          page: Number(filters.page) || 1,
          limit: Number(filters.limit) || 10,
          totalItems:
            paginationSource.totalItems ??
            paginationSource.total ??
            data?.result?.totalItems ??
            data?.totalItems ??
            data?.total ??
            items.length,
          totalPages: paginationSource.totalPages ?? data?.result?.totalPages ?? 1,
        },
        summary: data?.result?.summary || buildAdminSummary(items),
      },
    };
  },

  async createAdmin(payload) {
    const { data } = await axios.post("/institute/create", payload);

    return {
      ...data,
      result: normalizeAdmin(data?.result || {}),
    };
  },

  async getWhiteLabelAdmins(filters = {}) {
    const { data } = await axios.get("/admins", {
      params: buildWhiteLabelAdminQueryParams(filters),
    });
    const items = extractListItems(data).map((admin) => normalizeAdmin(admin));
    const paginationSource = data?.result?.pagination || data?.pagination || {};

    return {
      ...data,
      result: {
        ...(data?.result || {}),
        items,
        pagination: {
          page: Number(filters.page) || 1,
          limit: Number(filters.limit) || 10,
          totalItems:
            paginationSource.totalItems ??
            paginationSource.total ??
            data?.result?.totalItems ??
            data?.totalItems ??
            data?.total ??
            items.length,
          totalPages: paginationSource.totalPages ?? data?.result?.totalPages ?? 1,
        },
        summary: data?.result?.summary || buildAdminSummary(items),
      },
    };
  },

  async createWhiteLabelAdmin(payload) {
    const { data } = await axios.post("/admins", payload);

    return {
      ...data,
      result: normalizeAdmin(data?.result || data?.admin || data?.data || {}),
    };
  },

  async updateWhiteLabelAdmin(adminId, payload) {
    const { data } = await axios.put(`/admins/${adminId}`, payload);

    return {
      ...data,
      result: normalizeAdmin(data?.result || data?.admin || data?.data || {}),
    };
  },

  async updateAdmin(adminId, payload) {
    const { data } = await axios.put(`/institute/${adminId}`, payload);

    return {
      ...data,
      result: normalizeAdmin(data?.result || data?.institute || data?.data || {}),
    };
  },

  async updateAdminStatus(adminId, status) {
    const { data } = await axios.patch(`/admins/${adminId}/status`, {
      status,
    });

    return {
      ...data,
      result: normalizeAdmin(data?.result || {}),
    };
  },

  async retryAdhyayiProvision(adminId, password) {
    const { data } = await axios.post(
      `/admins/${adminId}/retry-adhyayi-provisioning`,
      {
        password,
      },
    );

    return {
      ...data,
      result: normalizeAdmin(data?.result || {}),
    };
  },

  async getTenantFeatures(tenantId) {
    const { data } = await axios.get(`/tenants/${tenantId}/features`);

    return {
      ...data,
      result: normalizeFeaturePayload(data?.result || {}),
    };
  },

  async updateTenantFeatures(tenantId, featureFlags) {
    const { data } = await axios.put(`/tenants/${tenantId}/features`, featureFlags);

    return {
      ...data,
      result: normalizeFeaturePayload(data?.result || {}),
    };
  },
};
